import React, { useEffect, useRef } from 'react'
import { Smartico } from '../../core/Smartico'
import { useAppSelector } from '../../redux/hooks'
import { RootState } from '../../redux/store'
import { LoginStatus } from '../../redux/slices/userSlice'
import { cacheBustWidgetUrl } from '../../core/widgets'
import './GamificationBlock.scss'

const FILL_SHADOW_STYLE = `
	:host {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}
	[data-smartico-react-root],
	#root {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		min-height: 0;
		height: 100%;
		overflow: hidden;
	}
	.app-root {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		min-height: 0;
		height: 100%;
		overflow: hidden;
	}
`

// The published widget.js mounts React into [data-smartico-react-root] inside
// an open shadow root. That node is a flex *item* of :host but is not itself
// a flex container, so .app-root { flex:1 } never resolves and the widget
// grows to content height. Inject the missing flex-fill chain after mount.
function applyFillToShadowHost(lightHost: HTMLElement) {
	const wrapper = lightHost.querySelector('[data-smartico-lp-widget]') as HTMLElement | null
	if (!wrapper?.shadowRoot) return false

	wrapper.style.height = '100%'
	wrapper.style.minHeight = '0'
	wrapper.style.flex = '1 1 0'
	wrapper.style.overflow = 'hidden'

	if (!wrapper.shadowRoot.querySelector('style[data-sm-fill]')) {
		const style = document.createElement('style')
		style.setAttribute('data-sm-fill', '')
		style.textContent = FILL_SHADOW_STYLE
		wrapper.shadowRoot.appendChild(style)
	}

	const reactRoot = wrapper.shadowRoot.querySelector('[data-smartico-react-root], #root') as HTMLElement | null
	if (reactRoot) {
		reactRoot.style.display = 'flex'
		reactRoot.style.flexDirection = 'column'
		reactRoot.style.flex = '1 1 auto'
		reactRoot.style.minHeight = '0'
		reactRoot.style.height = '100%'
		reactRoot.style.overflow = 'hidden'
	}

	return true
}

interface GamificationBlockProps {
	url: string
	domId: string
	fit?: 'content' | 'fill'
	className?: string
	style?: React.CSSProperties
	cacheBust?: boolean
}

/**
 * Mounts a Vibe Studio widget via `_smartico.gamificationBlock`.
 * Requires an identified session (init already ran in Smartico.identify).
 * See https://help.smartico.ai/welcome/products/vibe-studio-beta/widgets
 */
const GamificationBlock: React.FC<GamificationBlockProps> = ({ url, domId, fit = 'content', className, style, cacheBust = true }) => {
	const { loggedIn, user_id } = useAppSelector((state: RootState) => state.user)
	const ready = loggedIn === LoginStatus.LoggedIn && !!user_id
	const hostRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!ready) return

		const host = hostRef.current
		if (!host) return

		let cancelled = false
		const observer = new MutationObserver(() => {
			if (fit === 'fill' && applyFillToShadowHost(host)) {
				observer.disconnect()
			}
		})
		observer.observe(host, { childList: true, subtree: true })

		Smartico.apiReady.then(() => {
			if (cancelled) return
			const lib = Smartico.lib()
			if (!lib?.gamificationBlock) {
				console.error('Smartico.gamificationBlock is not available on this SDK build')
				return
			}
			lib.gamificationBlock({ dom_id: host.id, url: cacheBust ? cacheBustWidgetUrl(url) : url, fit })
		})

		return () => {
			cancelled = true
			observer.disconnect()
			host.innerHTML = ''
		}
	}, [ready, domId, url, fit, cacheBust])

	if (!ready) return null

	return <div ref={hostRef} id={domId} className={`gamification-block ${className ?? ''}`.trim()} style={style} />
}

export default GamificationBlock
